(ns app.front.view.dispatch.dispatch-booking
  (:require [app.domain.inspection.model :as inspection-model]
            [app.domain.municipality.model :as municipality-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.state-machine :as sm]
            [app.front.ui :as ui]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.create-application.gis-search :as gis-search]
            [app.front.view.dispatch.subs :as dispatch-subs]
            [app.front.view.group.subs :as group-subs]
            [app.front.view.inspections.add :as inspection-add]
            [app.front.view.inspections.booking-utils :as butil]
            [app.front.view.inspections.events :as inspection-events]
            [app.front.view.inspections.subs :as inspection-sub]
            [app.front.view.workspace.add-dummy :as add-dummy-ws]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(def interceptors [(rf/inject-cofx :tr)])

(def *slot-scroll-ref (r/atom nil))

(def state-machine
  {nil                  {:signal/reset ::no-results}
   ::no-results         {:signal/search ::searching}
   ::searching          {:signal/search-success ::search-results
                         :signal/search-failure ::no-results}
   ::search-results     {:signal/search              ::searching
                         :signal/reset               ::no-results
                         :signal/select-workspace    ::choose-inspection
                         :signal/start-add-workspace ::creating-workspace}
   ::creating-workspace {:signal/select-workspace ::choose-inspection}
   ::choose-inspection  {:signal/select-inspection ::enter-optional
                         :signal/change-workspace  ::search-results}
   ::enter-optional     {:signal/change-workspace  ::search-results
                         :signal/change-inspection ::choose-inspection}})


(def update-next-state (sm/make-update-next-state state-machine [::state :current-state]))


(rf/reg-sub ::state
  (fn [db _]
    (-> db ::state)))

(rf/reg-sub ::fetching?
  :<- [::state]
  (fn [state _]
    (some? (:fetching state))))

(rf/reg-sub ::current-state
  :<- [::state]
  (fn [state _]
    (-> state :current-state)))

(rf/reg-event-fx ::init
  interceptors
  (fn [{:keys [db]} _]
    {:db       (-> db
                   (dissoc ::state)
                   (update-next-state :signal/reset))
     :dispatch [::add-dummy-ws/clear]}))


(rf/reg-event-fx ::select-workspace
  (fn [{:keys [db]} [_ workspace-id]]
    {:db           (-> db
                       (assoc-in [::state :fetching] workspace-id))
     :comm/command {:command    :workspace/get
                    :data       {:workspace/id workspace-id
                                 :find-related [:entity.type/inspection
                                                :entity.type/verdict
                                                :entity.type/property]}
                    ;; time slots are part of the groups-for-dispatching request
                    :on-success [::fetch-workspace-success workspace-id]}}))


(rf/reg-event-fx ::fetch-workspace-success
  (fn [{:keys [db]} [_ workspace-id status data]]
    (let [ents             (-> data :entities)
          workspace        (p/find-first (vals ents) #(= (:workspace/id %) workspace-id))
          target           (p/find-first (vals ents) #(and (= (:entity/parent %) (:db/id workspace))
                                                           (= (:entity/type %) :entity.type/execution))) ;;TODO get target better
          find-entity-data #(get ents %)
          authority-group  (find-entity-data (:domain/authority-group workspace))
          country          (-> workspace :workspace/municipality find-entity-data :municipality/country)]
      {:db         (-> db
                       (assoc-in [::state :target] target)
                       (assoc-in [::state :selected-workspace] workspace)
                       (assoc :view/workspace (merge {:workspace/id workspace-id
                                                      :group/id     (:group/id authority-group)
                                                      :permit-type  (:workspace/permit-type workspace)}
                                                     (select-keys data [:group-features])))
                       (update-next-state :signal/select-workspace)
                       (assoc-in [::state :fetching] nil))
       :dispatch-n [[::entities/merge-entity-db status ents]
                    [::process/fetch-process-states
                     country
                     (:workspace/permit-type workspace)
                     (:process-flow data)]
                    [::inspection-events/fetch-inspections workspace-id]]})))

(rf/reg-event-fx ::inspection-added
  (fn [_ [_ changes]]
    {:dispatch [::select-inspection (entities/id-of-some-in-apply-changes
                                      :entity.type/inspection
                                      changes)]}))

(rf/reg-sub ::search-term
  :<- [::state]
  (fn [state _]
    (or (-> state :search-term) "")))

(rf/reg-sub ::inspection-parent
  :<- [::entities/context]
  :<- [::state]
  (fn [[ctx state]]
    (entities/find-entity ctx (-> state :target :db/id))))

(rf/reg-sub ::inspection-parent-id
  :<- [::inspection-parent]
  :-> :db/id)


(rf/reg-sub ::inspections
  :<- [::entities/context]
  :<- [::inspection-parent-id]
  (fn [[ctx parent-id] _]
    (->> (entities/find-entities ctx {:entity/type   :entity.type/inspection
                                      :entity/parent parent-id})
         (sort-by :ordered/order <))))


(rf/reg-event-fx ::search
  (fn [{:keys [db]} [_ term]]
    (let [auth-group-ids (->> db
                              :session/user
                              :user/authority-groups
                              (map :group/id)
                              set)]
      {:db           (-> db (update-next-state :signal/search))
       :comm/command {:command    :workspace/find
                      :data       (-> {:conditions     [{:filter.condition/attribute :domain/state
                                                         ;; Only fetch "open permits"
                                                         :filter.condition/values    #{:execution.state/permit-issued
                                                                                       :execution.state/construction-started
                                                                                       :execution.state/construction-stopped
                                                                                       :execution.state/occupied
                                                                                       :execution.state/new-code-enforcement
                                                                                       :execution.state/active-code-enforcement
                                                                                       :execution.state/approved
                                                                                       :execution.state/inspections-started
                                                                                       :execution.state/active}
                                                         :filter.condition/operator  :equals}
                                                        {:filter.condition/attribute :group/id
                                                         :filter.condition/values    auth-group-ids
                                                         :filter.condition/operator  :equals}]
                                       :sort-condition [:submitted :desc]
                                       :skip           0
                                       :limit          50}
                                      (p/assoc-if :search-term term))
                      :on-success [::search-success]}})))


(rf/reg-event-fx ::search-success
  interceptors
  (fn [{:keys [db]} [_ _ results]]
    {:db (-> db
             (assoc-in [::state :search-results] results)
             (update-next-state :signal/search-success))}))

(rf/reg-sub ::search-results
  :<- [::state]
  (fn [state _]
    (let [search-results (-> state :search-results)
          total-count    (-> search-results :total-count)
          results        (-> search-results :results)
          workspaces     (map (fn [workspace]
                                {:address           (-> workspace :workspace/address)
                                 :municipality-name (-> workspace :municipality/names)
                                 :owners            (-> workspace :property-owners)
                                 :applicants        (-> workspace :applicants)
                                 :workspace-id      (-> workspace :workspace/id)
                                 :municipal-case-id (-> workspace :workspace/municipal-case-id)
                                 :permit-type       (-> workspace :workspace/permit-type)
                                 :permit-type-names (-> workspace :workspace/permit-type-names)})
                              results)]
      (util/mmap total-count workspaces))))


(rf/reg-event-fx ::change-search-term
  interceptors
  (fn [{:keys [db]} [_ term]]
    (if-not (= term "")
      {:dispatch-debounce [{:id      ::do-search
                            :timeout 500
                            :action  :dispatch
                            :event   [::search term]}]
       :db                (-> db
                              (assoc-in [::state :search-term] term)
                              (update-next-state :signal/search))}
      {:db (-> db
               (p/dissoc-in [::state :search-term])
               (p/dissoc-in [::state :search-results]))})))


(rf/reg-event-db ::reset-search
  (fn [db _]
    (-> db
        (p/dissoc-in [::state :search-term])
        (p/dissoc-in [::state :search-results])
        (update-next-state :signal/reset))))


(rf/reg-sub ::selected-workspace
  :<- [::state]
  (fn [state _]
    (let [workspace (-> state :selected-workspace)]
      {:address            (-> workspace :workspace/address)
       :municipality       (-> workspace :workspace/municipality)
       :municipal-case-id  (-> workspace :workspace/municipal-case-id)
       :workspace-id       (-> workspace :workspace/id)
       :authority-group-id (-> workspace :domain/authority-group)
       :permit-type        (-> workspace :workspace/permit-type)})))


(rf/reg-event-db ::change-workspace
  (fn [db _]
    (-> db
        (update-next-state :signal/change-workspace))))

(rf/reg-event-fx ::start-add-workspace
  (fn [{:keys [db]} _]
    ;; Use the same default target as the workspace form, including its group filtering and municipality ordering.
    (let [{:keys [domain-group-id municipality]}
          (->> ((juxt ::dispatch-subs/dispatcher-authority-groups
                      ::dispatch-subs/selected-authority-group-id
                      ::dispatch-subs/use-unified-view?)
                db)
               dispatch-subs/ag-ids+dg-ids+scopes+prop-i18n-mapping-for-create
               add-dummy-ws/targets-handler
               first)]
      {:fx [(when municipality
              [:dispatch [::gis-search/address-search-initial
                          domain-group-id
                          municipality
                          (-> db ::state :search-term)]])]
       :db (update-next-state db :signal/start-add-workspace)})))

(rf/reg-event-db ::select-inspection
  (fn [db [_ id]]
    (when-not (= "none" id)
      (-> db
          (assoc-in [::state :selected-inspection-id] id)
          (assoc-in [::state :date-time-slots] #{})
          (assoc-in [::state :edited-date-time-slot] {:date         (js/Date.)
                                                      :time-slot-id nil})
          (update-next-state :signal/select-inspection)))))


(rf/reg-sub ::selected-inspection-id
  :<- [::state]
  (fn [state _]
    (-> state :selected-inspection-id)))


(rf/reg-sub ::selected-inspection
  :<- [::entities/context]
  :<- [::selected-inspection-id]
  (fn [[ctx inspection-id] _]
    (entities/find-entity ctx {:entity/type :entity.type/inspection
                               :db/id       inspection-id})))


(rf/reg-sub ::selected-inspection-with-status
  :<- [::selected-inspection]
  :-> butil/inspection-with-last-visit-status)


(rf/reg-event-db ::change-inspection
  (fn [db _]
    (-> db
        (update-next-state :signal/change-inspection))))


(rf/reg-event-db ::select-date
  (fn [db [_ date]]
    (-> db
        (assoc-in [::state :edited-date-time-slot :date] date)
        (update-next-state :signal/select-date))))


(rf/reg-sub ::selected-date
  :<- [::edited-date-time-slot]
  :-> :date)


(rf/reg-event-fx ::select-time-slot
  (fn [{:keys [db]} [_ time-slot]]
    {:fx [[::scroll-to-the-end]]
     :db (let [date-time-slot (get-in db [::state :edited-date-time-slot])]
           (-> db
               (update-in [::state :date-time-slots] (comp set conj) (assoc date-time-slot :time-slot-id time-slot))
               (p/dissoc-in [::state :edited-date-time-slot])))}))


(rf/reg-event-db ::change-date-time-slot
  (fn [db [_ slot]]
    (-> db
        (update-in [::state :date-time-slots] disj slot)
        (assoc-in [::state :edited-date-time-slot] slot))))


(rf/reg-fx ::scroll-to-the-end
  (fn [_ _]
    (r/after-render
      (fn []
        (when-let [el @*slot-scroll-ref]
          (set! (.-scrollTop el) (.-scrollHeight el)))))))

(rf/reg-event-db ::add-date-time-slot
  (fn [db _]
    (-> db
        (assoc-in [::state :edited-date-time-slot] {:date         nil
                                                    :time-slot-id nil}))))

(rf/reg-event-db ::cancel-date-time-slot-add
  (fn [db _]
    (-> db
        (p/dissoc-in [::state :edited-date-time-slot]))))

(rf/reg-event-db ::remove-date-time-slot
  (fn [db [_ slot]]
    (-> db
        (update-in [::state :date-time-slots] disj slot))))

(rf/reg-sub ::request-time-slots
  :<- [::dispatch-subs/dispatcher-authority-groups]
  :<- [::selected-date]
  :<- [::selected-workspace]
  :<- [::entities/entity-db]
  (fn [[group-data date {:keys [authority-group-id]} entity-db] _]
    (if date
      (->> (->> (get-in group-data [authority-group-id :availabilities])
                (map entity-db)
                (butil/valid-availability date)
                :availability/time-slots
                (map entity-db)
                (butil/time-slots-for-weekday (.getDay date)))
           (sort-by :time-slot/start-hour))
      [])))


(rf/reg-sub ::selected-time-slot
  :<- [::entities/context]
  (fn [ctx [_ time-slot-id]]
    (entities/find-entity ctx {:db/id time-slot-id})))


(rf/reg-sub ::group-tz
  :<- [::dispatch-subs/dispatcher-authority-groups]
  :<- [::selected-workspace]
  (fn [[time-slot-data {:keys [authority-group-id]}] _]
    (get-in time-slot-data [authority-group-id :group/tz])))


(rf/reg-sub ::unqualified-inspectors
  :<- [::dispatch-subs/visible-inspectors]
  :<- [::inspection-parent]
  :<- [::selected-inspection]
  (fn [[inspectors target inspection]]
    (->> inspectors
         (remove #(inspection-model/no-inspection-qualification-restriction? (util/mmap {:user %}
                                                                                        target
                                                                                        inspection)))
         set)))


(rf/reg-event-db ::select-inspector
  (fn [db [_ id]]
    (-> db
        (assoc-in [::state :selected-inspector-id] id))))


(rf/reg-sub ::selected-inspector-id
  :<- [::state]
  (fn [state _]
    (-> state :selected-inspector-id)))


(rf/reg-sub ::selected-inspector
  :<- [::entities/context]
  :<- [::selected-inspector-id]
  (fn [[ctx inspector-id] _]
    (entities/find-entity ctx {:entity/type :entity.type/user
                               :db/id       inspector-id})))


(rf/reg-event-db ::set-notes
  (fn [db [_ notes]]
    (assoc-in db [::state :notes] notes)))


(rf/reg-event-fx ::save
  (fn [{{state ::state} :db} _]
    (butil/create-booking-command {:target-id       (-> state :target :db/id)
                                   :on-success      [::save-success (when (:selected-inspector-id state)
                                                                      :booking/booking-dispatch-success)]
                                   :inspection-id   (:selected-inspection-id state)
                                   :date-time-slots (:date-time-slots state)
                                   :inspector-id    (:selected-inspector-id state)
                                   :notes           (:notes state)})))


(rf/reg-event-fx ::save-success
  interceptors
  (fn [{:keys [tr]} [_ msg status resp]]
    {:dispatch-n [[::entities/apply-changes status resp]
                  [::dispatch-subs/toggle-request-editor]
                  [:notifications/add :toast :success :success (tr [(or msg :booking/booking-success)])]]}))

(rf/reg-sub ::edited-date-time-slot
  :<- [::state]
  :-> :edited-date-time-slot)

(rf/reg-sub ::date-time-slots
  :<- [::state]
  (fn [state]
    (->> state
         :date-time-slots
         (sort-by :date))))

(rf/reg-sub ::reserved-dates
  :<- [::date-time-slots]
  (fn [date-time-slots]
    (->> date-time-slots
         (map :date)
         set)))

(rf/reg-sub ::invalid-data?
  :<- [::state]
  (fn [{:keys [date-time-slots]}]
    (or (empty? date-time-slots)
        (->> date-time-slots
             (some (fn [{:keys [date time-slot-id]}]
                     (or (nil? date)
                         (nil? time-slot-id))))
             boolean))))

(defn render-search-field [state tr]
  (let [search-term (<sub [::search-term])]
    [:div.mx-2
     [:p.p-0.my-2.text-xs (tr [:booking/search-for-application])]
     [ui/search {:id                      "dispatching-application-search"
                 :container-class         "mb-2"
                 :label-text              (tr [:booking/search-for-application])
                 :placeholder             (tr [:booking/search-placeholder])
                 :close-button-label-text (tr [:booking/empty])
                 :default-value           ""
                 :value                   search-term
                 :auto-focus              true
                 :search-in-progress?     (= state ::searching)
                 :loading-text            (tr [:search/loading])
                 :clear-fn                #(>evt [::reset-search])
                 :light                   true
                 :on-key-up               (fn [e]
                                            (when (futil/esc-press? e)
                                              (>evt [::dispatch-subs/toggle-request-editor])))
                 :on-change               (fn [e] (rf/dispatch-sync [::change-search-term
                                                                     (-> e
                                                                         futil/tval
                                                                         str/lower-case)]))}]]))


(defn render-searching [tr]
  [:div.m-2 (tr [:booking/searching])])

(defn spinner []
  [:div.w-full.my-8.flex.justify-around
   (cc/loading-spinner {:with-overlay false})])

(defn render-case-id-and-type [{:keys [tr lang workspace-id municipal-case-id permit-type permit-type-names]}]
  (let [permit-type-label (or (i18n-model/get-text-from-entities-or-map permit-type-names lang)
                              (tr [permit-type]))]
    [:div.my-1
     [:div.text-xs.leading-tight (or municipal-case-id workspace-id)]
     [:div.text-xs.leading-tight permit-type-label]]))

(defn render-search-results [tr]
  (let [{:keys [workspaces total-count]
         :or   {workspaces  []
                total-count 0}}         (<sub [::search-results])
        lang                            (<sub [:language])]
    (cond
      (= total-count 0)
      [:div.m-2 (tr [:booking/no-applications-found])]

      (<sub [::fetching?])
      [spinner]

      :else
      [:<>
       [:div.m-2 (if (= total-count 1)
                   (tr [:booking/application-found])
                   (tr [:booking/applications-found] [total-count]))]
       (for [{:keys [address
                     workspace-id
                     municipality-name
                     municipal-case-id
                     owners
                     applicants
                     permit-type
                     permit-type-names]} workspaces]
         ^{:key workspace-id}
         [:button.p-2.block.w-full.border-0.bg-transparent.text-sm.text-left.hover:bg-gray-300
          {:on-click #(>evt [::select-workspace workspace-id])}
          [:div.text-sm.leading-tight.font-bold
           (->> [address (i18n-model/get-text municipality-name lang)]
                (remove nil?)
                (str/join ", "))]
          [render-case-id-and-type (util/mmap tr lang municipal-case-id workspace-id permit-type permit-type-names)]
          (when (seq applicants)
            [:div.m-0.text-xs.leading-tight.flex
             [:div.w-16.mr-2.flex-none
              (tr [(if (> (count applicants) 1)
                     :search/applicants
                     :search/applicant)])]
             [:div.flex-1
              (str/join ", " applicants)]])
          (when (seq owners)
            [:div.m-0.text-xs.leading-tight.flex.flex-none
             [:div.w-16.mr-2.flex-none
              (tr [(if (> (count owners) 1)
                     :search/owners
                     :search/owner)])]
             [:div.flex-1
              (str/join ", " owners)]])])])))


(defn- render-change-button [on-click tr]
  [:button.border-0.text-xs.font-bold.bg-transparent.hover:underline.opacity-50.hover:opacity-100.cursor-pointer
   {:on-click on-click} (tr [:booking/change])])


(defn render-part [{:keys [tr title href description on-change no-separator?]} & body]
  [:div (into [:div.m-2.mb-4
               [:p.p-0.mb-2.text-xs
                description
                [render-change-button #(>evt on-change) tr]]
               (if href
                 (cc/link
                   {:href       href
                    :target     "_blank"
                    :class-name "m-0 text-sm leading-tight font-bold"}
                   title)
                 [:h1.m-0.text-sm.leading-tight.font-bold title])]
              body)
   (when (not no-separator?)
     [:hr.border.border-ui-gray-30.mx-2])])

(defn render-selected-workspace [tr]
  (let [{:keys [address
                municipality
                municipal-case-id
                permit-type
                workspace-id]}    (<sub [::selected-workspace])
        municipality              (<sub [::entities/entity municipality])
        lang                      (<sub [:language])]
    [render-part {:tr          tr
                  :title       (->> [address (municipality-model/municipality-name municipality lang)]
                                    (remove nil?)
                                    (str/join ", "))
                  :href        (href/href :view/workspace-execution
                                          {:workspace-id workspace-id})
                  :description (tr [:booking/selected-application])
                  :on-change   [::change-workspace]}
     [render-case-id-and-type (util/mmap tr municipal-case-id workspace-id permit-type)]]))


(defn render-selected-inspection [tr]
  (let [{:keys [inspection visit]} (<sub [::selected-inspection-with-status])
        active-booking             (<sub [::active-booking-of-the-inspection (:db/id inspection)])
        lang                       (<sub [:language])]
    [render-part {:tr          tr
                  :title       (inspection-model/inspection-name inspection lang)
                  :description (tr [:booking/selected-inspection])
                  :on-change   [::change-inspection]}
     [:<>
      [:span.font-normal (str " (" (butil/inspection-result-or-booking-to-string tr lang active-booking visit) ")")]
      (when (:inspection/recurring? inspection)
        [:p.mt-4 (tr [:booking/recurring-inspection] [(:inspection/recurrence-interval inspection)])])]]))


(defn render-selected-date [tr date-time-slot]
  (let [format-date (<sub [::date-util/date-fn])]
    [render-part {:tr            tr
                  :title         (-> date-time-slot :date format-date)
                  :description   (tr [:booking/selected-date])
                  :on-change     [::change-date-time-slot date-time-slot]
                  :no-separator? true}]))


(defn render-selected-time-slot [tr date-time-slot]
  (let [time-slot (<sub [::selected-time-slot (:time-slot-id date-time-slot)])
        group-tz  (<sub [::group-tz])]
    [render-part {:tr          tr
                  :title       (butil/time-slot-string time-slot group-tz)
                  :description (tr [:booking/selected-time-slot])
                  :on-change   [::change-date-time-slot date-time-slot]}]))


(rf/reg-sub ::inspections-with-statuses
  :<- [::inspections]
  (fn [inspections _]
    (let [;; Only inspections that are not passed yet are valid here
          passed-inspection-pred (fn [inspection]
                                   (->> inspection
                                        inspection-model/inspection-visits
                                        inspection-model/some-visit-passed?
                                        not))
          available-inspections  (filter passed-inspection-pred inspections)]
      (map butil/inspection-with-last-visit-status available-inspections))))


(rf/reg-sub ::active-booking-of-the-inspection
  (fn [[_ inspection-id]]
    (rf/subscribe [::entities/entities {:entity/type   :entity.type/booking
                                        :entity/parent inspection-id}]))
  (fn [bookings _]
    (-> bookings
        (p/find-first (comp #{:booking.state/to-dispatch :booking.state/dispatched}
                            :domain/state)))))


(rf/reg-sub ::fetching-inspections?
  :<- [::selected-workspace]
  :<- [::inspection-sub/fetching]
  (fn [[{:keys [workspace-id]} fetching-ids]]
    (contains? fetching-ids workspace-id)))


(rf/reg-sub ::multiple-bookings-enabled?
  :<- [::selected-workspace]
  (fn [{:keys [authority-group-id]}]
    @(rf/subscribe [::group-subs/has-feature? authority-group-id :group.feature/multiple-inspection-bookings-authority])))


(defn render-inspections [tr]
  (let [inspections-with-statuses (<sub [::inspections-with-statuses])
        lang                      (<sub [:language])]
    (cond
      (<sub [::fetching-inspections?])
      [spinner]

      (seq inspections-with-statuses)
      [:div.m-2
       (cc/select
         {:id         "search-variable-select"
          :class-name "full-width-select"
          :label-text (tr [:booking/choose-inspection])
          :light      true
          :on-change  #(>evt [::select-inspection (-> % futil/tval futil/parse-int)])}
         [ui/placeholder-select-item tr :none]
         ;; If an inspection has an active booking then display the inspection as disabled
         (futil/for-frag [{:keys [inspection visit]} inspections-with-statuses
                          :let [inspection-id   (-> inspection :db/id)
                                name            (inspection-model/inspection-name inspection lang)
                                active-booking  (<sub [::active-booking-of-the-inspection inspection-id])
                                active-booking? (boolean active-booking)
                                status          (butil/inspection-result-or-booking-to-string tr lang active-booking visit)
                                title           (str name ": " status)]
                          :when (or active-booking?
                                    ;; TODO WIN-2781 application too in some cases!
                                    (<sub [::process/execution-signal-permitted?
                                           [:process.signal/create-booking {:inspection-id inspection-id}]]))]
           (cc/select-item {:text     title
                            :value    inspection-id
                            :disabled active-booking?})))])))


(defn render-date-input [tr date-time-slot can-remove?]
  (let [time-slots     (<sub [::request-time-slots])
        reserved-dates (<sub [::reserved-dates])
        group-tz       (<sub [::group-tz])]
    [:div.flex.flex-col.m-2.gap-y-2
     (when can-remove?
       [:div.flex.flex-col.items-end
        [cc/button {:kind       :ghost
                    :size       :small
                    :icon       i/trash
                    :class-name "float-right w-auto"
                    :on-click   #(>evt [::cancel-date-time-slot-add])}
         (tr [:booking/remove-date])]])
     [ui/date-picker {:id          "booking-date"
                      :callback    #(>evt [::select-date %])
                      :label-text  (tr [:booking/requested-date])
                      :value       (:date date-time-slot)
                      :min-date    ((<sub [::date-util/date-fn]) (js/Date.))
                      :allow-date? #(-> reserved-dates (contains? %) not)
                      :width       "100%"
                      :class-input "bg-white"}]
     (cc/select
       {:key        "time-slot"
        :id         "time-slot"
        :label-text (tr [:booking/requested-time-slot])
        :value      (:time-slot-id date-time-slot)
        :class-name "booking-time-slot"
        :light      true
        :on-change  #(>evt [::select-time-slot (-> % futil/tval futil/parse-int)])
        :required   true}
       [ui/placeholder-select-item tr :none]
       (futil/for-frag [slot time-slots]
         (cc/select-item {:key   (str (:db/id slot))
                          :text  (butil/time-slot-string slot group-tz)
                          :value (:db/id slot)})))]))

(defn render-date-time-slots [tr]
  (let [date-time-slots       (<sub [::date-time-slots])
        edited-date-time-slot (<sub [::edited-date-time-slot])
        can-remove?           (-> date-time-slots rest seq)
        can-cancel-add?       (-> date-time-slots seq)
        can-add?              (and (<sub [::multiple-bookings-enabled?])
                                   (seq date-time-slots)
                                   (not edited-date-time-slot))]
    [:div.flex.flex-col.gap-y-1
     [:div.overflow-y-auto.max-h-80
      {:ref #(reset! *slot-scroll-ref %)}
      (futil/for-frag [slot date-time-slots]
        [:div
         [:div.flex.flex-col.items-end
          (when can-remove?
            [cc/button {:kind       :ghost
                        :size       :small
                        :icon       i/trash
                        :class-name "float-right w-auto"
                        :on-click   #(>evt [::remove-date-time-slot slot])}
             (tr [:booking/remove-date])])]
         [render-selected-date tr slot]
         [render-selected-time-slot tr slot]])]
     (when edited-date-time-slot
       [render-date-input tr edited-date-time-slot can-cancel-add?])
     (when can-add?
       [:div.flex
        (cc/button
          {:kind       :tertiary
           :size       :small
           :icon       i/plus
           :class-name "m-2"
           :on-click   #(>evt [::add-date-time-slot])}
          (tr [:booking/add-date]))])]))

(defn render-optional-inputs [tr]
  (let [inspectors             (<sub [::dispatch-subs/visible-inspectors])
        unqualified-inspectors (<sub [::unqualified-inspectors])
        selected-inspector     (<sub [::selected-inspector])]
    [:div.flex.flex-col.m-2.gap-y-4
     (when (ff/enabled? :feature-flag.id/dispatch-assign)
       [ui-select/select {:id              "inspector-select"
                          :class-name      "full-width-select"
                          :empty-text      (tr [:none])
                          :label-text      (tr [:booking/assign-inspector])
                          :value           selected-inspector
                          :on-change       #(>evt [::select-inspector (:db/id %)])
                          :val->str        user-model/full-name
                          :values          inspectors
                          :disabled-values unqualified-inspectors}])
     [ui/text-area {:id          "booking-notes"
                    :class       "mb-4"
                    :label-text  (tr [:booking/notes])
                    :placeholder (tr [:booking/notes-placeholder])
                    :light       true
                    :on-change   #(>evt [::set-notes (futil/tval %)])}]
     (cc/button
       {:kind     :primary
        :on-click #(>evt [::save])
        :disabled (<sub [::invalid-data?])}
       (tr [:booking/create-booking]))]))


(defn begin-add-workspace-button [tr]
  (when-not (<sub [::fetching?])
    (cc/button
      {:href             "#"
       :class-name       "w-full justify-start"
       :kind             :ghost
       :icon             i/plus
       :icon-description ""
       :on-click         #(>evt [::start-add-workspace])}
      (tr [:workspace/create]))))

(defn render-add-inspection-selector [{:keys [tr]}]
  (r/with-let [*visible? (r/atom false)]
    (when-not (<sub [::fetching-inspections?])
      (if @*visible?
        (let [target (<sub [::inspection-parent])]
          [:div.ml-2
           [:div.mb-2 (tr [:inspection/add-new])]
           [inspection-add/add-selector
            (util/mmap {:add-as-active?  true
                        :on-add-dispatch [::inspection-added]
                        :target-entity   target
                        :light           true}
                       tr)]])
        (cc/button
          {:kind       :primary
           :size       :field
           :on-click   #(reset! *visible? true)
           :class-name "ml-2"}
          (tr [:inspection/add-new]))))))

(defn render-create-workspace []
  [add-dummy-ws/create-workspace
   {:on-created-dispatch                                 [::select-workspace]
    :group-ids+domain-group-ids+scopes+prop-i18n-mapping (<sub [::dispatch-subs/ag-ids+dg-ids+scopes+prop-i18n-mapping-for-create])}])

(defmulti render-state (fn [state _] state))

(defmethod render-state ::no-results [state tr]
  [render-search-field state tr])

(defmethod render-state ::searching [state tr]
  [:<>
   [render-search-field state tr]
   [render-searching tr]])

(defmethod render-state ::search-results [state tr]
  [:<>
   [render-search-field state tr]
   [render-search-results tr]
   [begin-add-workspace-button tr]])

(defmethod render-state ::creating-workspace [_ _]
  [render-create-workspace])

(defmethod render-state ::choose-inspection [_ tr]
  [:<>
   [render-selected-workspace tr]
   [render-inspections tr]
   [render-add-inspection-selector {:tr tr}]])

(defmethod render-state ::enter-optional [_ tr]
  [:<>
   [render-selected-workspace tr]
   [render-selected-inspection tr]
   [render-date-time-slots tr]
   [render-optional-inputs tr]])

(defmethod render-state :default [_ _])

(defn search [tr]
  (let [state (<sub [::current-state])]
    [eb/error-boundary
     [:div.inline-booking
      [:h1.m-2.mb-4.text-base.leading-tight.font-bold (tr [:booking/new-booking])]
      (render-state state tr)]]))
